/* eslint-disable max-lines */
import { animated, useSpring } from "@react-spring/web";
import { useAtom, useSetAtom } from "jotai";
import { Info, Loader, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import {
  useArtworkForm,
  useDtfPricing,
  useEnhancedImageSource
} from "@design-lab/entities/artwork";
import {
  currentPinningSessionAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  useCancelPinningSession,
  useCreatePinningSession
} from "@design-lab/entities/pinning";
import { isRecaptureTaskPendingAtom } from "@design-lab/features/user-retention";
import {
  trackDesignLabPinningInstructionsRemoved,
  trackDesignLabPinningModeEntered,
  trackDesignLabPinsCtaClicked,
  trackDesignLabPinningSessionSummaryEdited
} from "@design-lab/shared/lib/analytics";
import { useDesignLabConfig, usePinBasedRedrawEnabled } from "@design-lab/shared/lib/config";
import { formatCurrency } from "@design-lab/shared/lib/currency";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { Badge, Button, Checkbox, Label, Switch, UnionIcon, useToast } from "@design-lab/shared/ui";
import { MarkdownContent } from "@design-lab/shared/ui/MarkdownContent";
import { SwitchInput } from "@design-lab/shared/ui/SwitchInput";
import { Textarea } from "@design-lab/shared/ui/textarea";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";

import { highlightJiffyDesignServiceAtom } from "@design-lab/shared/state";
import { StarsIcon } from "@design-lab/shared/ui/icons";
import { REDRAW_NOTES_MAX_LENGTH } from "common/redraw-constants";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { DesignServiceInfo } from "../DesignServiceInfo";
import { PrintPerfectExplanatoryTooltip } from "./PrintPerfectExplanatoryTooltip";

// Helper to convert base64 data URL to Blob
function base64ToBlob(base64: string): Blob | null {
  try {
    const parts = base64.split(",");
    const mimeMatch = parts[0]?.match(/:(.*?);/);
    if (!mimeMatch || !parts[1]) return null;

    const mime = mimeMatch[1] || "image/png";
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);

    for (let i = 0; i < bstr.length; i += 1) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new Blob([u8arr], { type: mime });
  } catch {
    return null;
  }
}

export type PrintPerfectProps = {
  canRemoveBackground?: boolean;
  showRefineArtwork?: boolean;
  canEditCutoutModal?: boolean;
  // Form-related props
  removeBackground: boolean;
  jiffyDesignServices: boolean;
  enhancementRequestNote?: string | undefined;
  editImageManual: boolean;
  instructionsError?: string | undefined;
  placementSide?: "front" | "back";
  // Handlers
  onRemoveBackgroundChange: (checked: boolean) => void;
  onJiffyDesignServicesChange: (checked: boolean) => void;
  onEnhancementRequestNoteChange: (value: string) => void;
  onRefineArtworkClick?: undefined | (() => void);
  onResetManualEdit?: undefined | (() => void);
  // Form registration for the textarea
  enhancementRequestNoteField: any;
  // Lens for SwitchInput (optional - only when used in design lab)
  removeBackgroundLens?: any;
};

// eslint-disable-next-line max-lines-per-function
export function PrintPerfect({
  canRemoveBackground = true,
  showRefineArtwork = true,
  removeBackground,
  jiffyDesignServices,
  editImageManual,
  instructionsError,
  onRemoveBackgroundChange,
  onJiffyDesignServicesChange,
  onRefineArtworkClick,
  onResetManualEdit,
  enhancementRequestNoteField,
  removeBackgroundLens,
  enhancementRequestNote,
  placementSide = "front"
}: PrintPerfectProps) {
  const [isConfirmToggleOpen, setIsConfirmToggleOpen] = useState(false);
  const [isConfirmResetOpen, setIsConfirmResetOpen] = useState(false);

  const { form } = useArtworkForm();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const { mutate: createPinningSession, isPending: isCreatingPinningSession } =
    useCreatePinningSession();
  const { mutate: cancelSession, isPending: isCancellingSession } = useCancelPinningSession();
  const { error: showErrorToast } = useToast();

  // Get the currently displayed enhanced image source
  const { source: imageSource } = useEnhancedImageSource({ side: placementSide });

  // Check if pin-based redraw is enabled for this user
  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();

  // Check if there's a session for the current artwork
  const currentArtworkId = form.watch(`${placementSide}.artworkId`) as string | undefined;
  const sessionForArtwork = currentArtworkId ? pinningSessions[currentArtworkId] : undefined;
  const hasActiveSession = pinBasedRedrawEnabled && sessionForArtwork?.state === "active";
  const hasCompletedSession = pinBasedRedrawEnabled && sessionForArtwork?.state === "completed";
  const completedSessionSummary = hasCompletedSession ? sessionForArtwork?.summary : null;

  const handleRequestDesignerEdit = () => {
    if (isCreatingPinningSession) return;
    const artworkId = form.getValues(`${placementSide}.artworkId`);
    if (!artworkId) return;

    trackDesignLabPinsCtaClicked(artworkId);

    // Reuse existing active session if one exists for this artwork
    const existingSession = pinningSessions[artworkId];
    if (existingSession?.state === "active") {
      trackDesignLabPinningModeEntered({
        artworkId,
        sessionId: existingSession.sessionId,
        hasExistingPins: true
      });
      setCurrentPinningSession({ artworkId, sessionId: existingSession.sessionId });
      setPinningMode(true);
      return;
    }

    // Capture the currently rendered image to send with the session
    const renderedImageBase64 = imageSource?.enhancedImage?.base64;
    const renderedImageBlob = renderedImageBase64 ? base64ToBlob(renderedImageBase64) : null;

    createPinningSession(
      { artworkId, renderedImage: renderedImageBlob ?? undefined },
      {
        onSuccess: data => {
          trackDesignLabPinningModeEntered({
            artworkId,
            sessionId: data.sessionId,
            hasExistingPins: false
          });
          setPinningSessions(prev => ({
            ...prev,
            [artworkId]: { sessionId: data.sessionId, state: "active", summary: null }
          }));
          setCurrentPinningSession({ artworkId, sessionId: data.sessionId });
          setPinningMode(true);
        },
        onError: () => {
          showErrorToast({
            title: "Unable to start design instructions",
            description: "Something went wrong. Please try again."
          });
        }
      }
    );
  };

  const jiffyDesignServicesId = useId();
  const designerInstructionsId = useId();
  const { data: pricingData } = useDtfPricing();
  const expertEditAddonTierPrice = pricingData?.expertEditAddonTierPrice;
  const [shouldHighlight, setShouldHighlight] = useAtom(highlightJiffyDesignServiceAtom);
  const jiffyDesignServicesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const config = useDesignLabConfig();
  const isCustomerReviewOnRedrawsEnabled =
    config?.flip?.customerReviewOnRedraws && config?.flip.customerReviewOnPrintRedraws;

  const scrollToTextAreaAndHighlight = useCallback(() => {
    const $textarea = textareaRef.current;

    if ($textarea) {
      $textarea.scrollIntoView({ behavior: "smooth", block: "center" });
      $textarea.focus();
      setTimeout(() => {
        $textarea.classList.add("animate-highlight-pulse");
      }, 100);
    }
  }, []);

  // Shimmer animation - declarative approach (repeats 3 times)
  const shimmerStyles = useSpring({
    from: { x: -100, opacity: 0 },
    to: shouldHighlight
      ? [
          // First cycle
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } },
          // Second cycle
          { x: -100, opacity: 0, config: { duration: 100 } },
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } },
          // Third cycle
          { x: -100, opacity: 0, config: { duration: 100 } },
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } }
        ]
      : { x: -50, opacity: 0 },
    onRest: () => {
      if (shouldHighlight) {
        setShouldHighlight(false);
      }
    },
    config: { tension: 280, friction: 60 }
  });

  // Handle scrolling when highlight is triggered
  useEffect(() => {
    if (!shouldHighlight) return;

    jiffyDesignServicesRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  }, [shouldHighlight]);

  const [isRecaptureTaskPending, setRecaptureTaskPending] = useAtom(isRecaptureTaskPendingAtom);
  const pendingScrollRef = useRef(false);

  // Handle pending recapture task when this component renders (after tab switch)
  useEffect(() => {
    if (!isRecaptureTaskPending) return;

    setRecaptureTaskPending(false);

    if (jiffyDesignServices) {
      scrollToTextAreaAndHighlight();
    } else {
      onJiffyDesignServicesChange(true);
      pendingScrollRef.current = true;
    }
  }, [
    isRecaptureTaskPending,
    setRecaptureTaskPending,
    onJiffyDesignServicesChange,
    jiffyDesignServices,
    scrollToTextAreaAndHighlight
  ]);

  const textareaCallbackRef = useCallback(
    ($node: HTMLTextAreaElement | null) => {
      textareaRef.current = $node;
      enhancementRequestNoteField?.ref?.($node);

      if ($node && pendingScrollRef.current) {
        pendingScrollRef.current = false;
        scrollToTextAreaAndHighlight();
      }
    },
    [enhancementRequestNoteField, scrollToTextAreaAndHighlight]
  );

  const formattedPrice =
    expertEditAddonTierPrice > 0 ? formatCurrency(expertEditAddonTierPrice) : "Free";

  const hideDesignServices = useMemo(() => pinBasedRedrawEnabled, [pinBasedRedrawEnabled]);

  return (
    <div className="flex flex-col gap-4">
      {canRemoveBackground && (
        <div className="flex items-center space-x-2">
          {removeBackgroundLens ? (
            <SwitchInput
              data-testid="remove-background-switch"
              id="remove-background"
              lens={removeBackgroundLens}
              onCheckedChange={checked => {
                // Show confirmation if there are manual edits that will be cleared
                if (editImageManual) {
                  setIsConfirmToggleOpen(true);
                  return;
                }
                onRemoveBackgroundChange(checked);
              }}
            />
          ) : (
            <Switch
              data-testid="remove-background-switch"
              id="remove-background"
              isSelected={removeBackground}
              onChange={checked => {
                // Show confirmation if there are manual edits that will be cleared
                if (editImageManual) {
                  setIsConfirmToggleOpen(true);
                  return;
                }
                onRemoveBackgroundChange(checked);
              }}
            />
          )}
          <Label data-testid="remove-background-label" htmlFor="remove-background">
            Remove background
          </Label>
        </div>
      )}

      {showRefineArtwork && (
        <div className="flex flex-col gap-2" data-testid="print-perfect-section">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              data-testid="refine-artwork-button"
              className="bg-cta-gradient hover:bg-cta-gradient-hover flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border-0 p-3 text-sm text-white"
              onClick={onRefineArtworkClick}
            >
              <UnionIcon className="h-4 w-4 text-white" />
              <span>Edit Design</span>
            </button>
            {editImageManual && (
              <button
                type="button"
                data-testid="reset-manual-edit-button"
                className="ml-3 flex cursor-pointer items-center gap-1 text-sm text-blue-700 hover:text-blue-800"
                onClick={() => setIsConfirmResetOpen(true)}
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reset</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pin-based redraw UI - rendered independently of showRefineArtwork */}
      {/* No session - show Request Designer Edit button */}
      {pinBasedRedrawEnabled && !hasCompletedSession && !hasActiveSession && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-neutral-950">
              How can we make your image better?
            </span>
          </div>
          <button
            type="button"
            data-testid="request-designer-edit-button"
            className="border-cta-gradient flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg !border-[1px] p-3 text-sm font-medium text-neutral-950 hover:![background-image:linear-gradient(#f3f4f6,#f3f4f6),linear-gradient(103.21deg,#a855f7_6.2%,#2563eb_108.53%)]"
            onClick={handleRequestDesignerEdit}
            disabled={isCreatingPinningSession}
          >
            <StarsIcon className="h-5 w-5" />
            <span>Request Design Changes</span>
          </button>
        </div>
      )}
      {/* Active session - show Resume/Cancel card */}
      {hasActiveSession && currentArtworkId && sessionForArtwork && (
        <div
          data-testid="active-session-indicator"
          className="flex flex-col gap-3 rounded-lg border-2 border-blue-400 bg-blue-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <span className="text-sm font-medium text-gray-900 sm:text-base">
            You have an unfinished design request
          </span>
          <div className="flex gap-2 self-end sm:self-auto">
            <Button
              type="button"
              data-testid="delete-active-session-button"
              className="shrink-0 cursor-pointer rounded-lg border-2 border-red-400 bg-transparent px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              isDisabled={isCancellingSession}
              onPress={() => {
                const { sessionId } = sessionForArtwork;
                const { artworkId } = { artworkId: currentArtworkId };
                cancelSession(sessionId, {
                  onSuccess: () => {
                    trackDesignLabPinningInstructionsRemoved({
                      artworkId,
                      sessionId
                    });
                    setPinningSessions(prev => {
                      const next = { ...prev };
                      delete next[artworkId];
                      return next;
                    });
                    form.setValue(`info.${placementSide}.enhancementRequestNote` as any, "");
                  }
                });
              }}
            >
              Delete
            </Button>
            <Button
              type="button"
              data-testid="continue-session-button"
              className="shrink-0 cursor-pointer rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
              onPress={() => {
                trackDesignLabPinningModeEntered({
                  artworkId: currentArtworkId,
                  sessionId: sessionForArtwork.sessionId,
                  hasExistingPins: true
                });
                setCurrentPinningSession({
                  artworkId: currentArtworkId,
                  sessionId: sessionForArtwork.sessionId
                });
                setPinningMode(true);
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Completed pinning session indicator */}
      {hasCompletedSession && (
        <div
          data-testid="completed-session-indicator"
          className="flex flex-col gap-3 rounded-lg border-2 border-green-400 bg-green-100 p-4"
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 sm:text-base">
                Design Services Requested.
              </span>
              <TooltipTrigger delay={300}>
                <button
                  type="button"
                  data-testid="design-services-info-tooltip"
                  className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
                  aria-label="Design services information"
                >
                  <Info className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                </button>
                <Tooltip
                  className="w-[315px] bg-blue-700 p-3 text-sm lg:w-fit lg:max-w-[430px]"
                  placement="top"
                  offset={8}
                  arrowClassName="fill-blue-700"
                >
                  <p className="text-wrap">
                    Our design team will perform the requested changes once the order is completed
                  </p>
                </Tooltip>
              </TooltipTrigger>
            </div>
            {completedSessionSummary && (
              <div className="text-sm text-gray-700">
                <MarkdownContent content={completedSessionSummary} />
              </div>
            )}
          </div>
          <div className="flex gap-2 self-end">
            <Button
              type="button"
              data-testid="cancel-design-request-button"
              className="shrink-0 cursor-pointer rounded-lg border-2 border-red-400 bg-transparent px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              isDisabled={isCancellingSession}
              onPress={() => {
                if (currentArtworkId && sessionForArtwork) {
                  const { sessionId } = sessionForArtwork;
                  const { artworkId } = { artworkId: currentArtworkId };
                  cancelSession(sessionId, {
                    onSuccess: () => {
                      trackDesignLabPinningInstructionsRemoved({
                        artworkId,
                        sessionId
                      });
                      setPinningSessions(prev => {
                        const next = { ...prev };
                        delete next[artworkId];
                        return next;
                      });
                      form.setValue(`info.${placementSide}.enhancementRequestNote` as any, "");
                    }
                  });
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="edit-design-request-button"
              className="shrink-0 cursor-pointer rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
              onPress={() => {
                if (!currentArtworkId || !sessionForArtwork) return;

                setCurrentPinningSession({
                  artworkId: currentArtworkId,
                  sessionId: sessionForArtwork.sessionId
                });

                if (completedSessionSummary) {
                  trackDesignLabPinningSessionSummaryEdited({
                    artworkId: currentArtworkId,
                    sessionId: sessionForArtwork.sessionId
                  });
                } else {
                  trackDesignLabPinningModeEntered({
                    artworkId: currentArtworkId,
                    sessionId: sessionForArtwork.sessionId,
                    hasExistingPins: true
                  });
                }
                setPinningMode(true);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      )}

      {!hideDesignServices && (
        <div
          ref={jiffyDesignServicesRef}
          className={cn(
            "relative flex items-center justify-between space-x-2 overflow-hidden rounded-lg bg-green-200 p-3 transition-all duration-300",
            shouldHighlight && "shadow-xl shadow-green-300/50 ring-2 ring-green-300"
          )}
        >
          {/* Primary shimmer element */}
          <animated.span
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
            style={{
              transform: shimmerStyles.x.to(x => `translateX(${x}%)`),
              opacity: shimmerStyles.opacity
            }}
          />
          {/* Secondary glow layer for extra vibrancy */}
          <animated.span
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-green-100/50 to-transparent blur-xl"
            style={{
              transform: shimmerStyles.x.to(x => `translateX(${x}%)`),
              opacity: shimmerStyles.opacity.to(o => o * 0.7)
            }}
          />
          <div className="flex flex-1 items-center space-x-2">
            <Checkbox
              data-testid="jiffy-design-services-switch"
              id={jiffyDesignServicesId}
              isSelected={jiffyDesignServices || false}
              onChange={checked => {
                const isChecked = checked === true;
                onJiffyDesignServicesChange(isChecked);
              }}
            />
            <div className="flex flex-col gap-2 lg:flex-row">
              <Label
                data-testid="jiffy-design-services-label"
                htmlFor={jiffyDesignServicesId}
                className="order-2 flex items-center gap-2 lg:order-1"
              >
                Jiffy Design Services
              </Label>
              {expertEditAddonTierPrice ? (
                <>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        "order-1 items-center gap-2 lg:order-2",
                        index === 0 ? "hidden lg:flex" : "flex lg:hidden"
                      )}
                    >
                      <Badge size="sm" className="bg-gray-700 font-medium">
                        Add-On
                      </Badge>
                      <p className="whitespace-nowrap text-sm text-green-700">{formattedPrice}</p>
                    </div>
                  ))}
                </>
              ) : (
                <Loader className="animate-spin" />
              )}
            </div>
            <div className="flex flex-1 justify-end">
              <PrintPerfectExplanatoryTooltip>
                <DesignServiceInfo />
              </PrintPerfectExplanatoryTooltip>
            </div>
          </div>
        </div>
      )}

      {!hideDesignServices && jiffyDesignServices && (
        <div className="flex flex-col gap-2">
          <Label data-testid="designer-instructions-label" htmlFor={designerInstructionsId}>
            <em className="text-red-500">*</em>&nbsp; Instructions for our designers
          </Label>
          <Textarea
            data-testid="designer-instructions-textarea"
            {...enhancementRequestNoteField}
            ref={textareaCallbackRef}
            id={designerInstructionsId}
            rows={4}
            maxLength={REDRAW_NOTES_MAX_LENGTH}
            placeholder='Example: "Change the color of the font to black. Remove the background from the top half of the image."'
            className={cn(instructionsError && "border-red-500")}
          />
          {instructionsError && <small className="text-red-500">{instructionsError}</small>}
          {enhancementRequestNote && isCustomerReviewOnRedrawsEnabled && (
            <span className="text-sm font-normal leading-5">
              <b>Note: </b>
              We will send a notification to review your redrawn artwork before printing. If there
              is no response within 1 hour, your order will proceed to print to prevent any further
              delays.
            </span>
          )}
        </div>
      )}
      <ConfirmActionModal
        isOpen={isConfirmToggleOpen}
        onClose={() => setIsConfirmToggleOpen(false)}
        title={`Turn ${removeBackground ? "Off" : "On"} Background Removal?`}
        message={`${removeBackground ? "Disabling" : "Enabling"} this will undo all background edits you've made. Do you want to continue?`}
        confirmText={`Turn ${removeBackground ? "Off" : "On"}`}
        cancelText="Keep Edits"
        onConfirm={() => {
          onRemoveBackgroundChange(!removeBackground);
          setIsConfirmToggleOpen(false);
        }}
      />
      <ConfirmActionModal
        isOpen={isConfirmResetOpen}
        onClose={() => setIsConfirmResetOpen(false)}
        title="Reset Edits?"
        message="This will discard all manual edits and restore the original image. Continue?"
        confirmText="Reset"
        cancelText="Keep Edits"
        onConfirm={() => {
          onResetManualEdit?.();
          setIsConfirmResetOpen(false);
        }}
      />
    </div>
  );
}
